import { useNavigate } from 'react-router-dom';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import SEO from '../../components/feature/SEO';

export default function PaymentFailed() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gradient-to-br from-red-50 via-orange-50 to-blue-50">
      <SEO title="Payment Failed – QBOVerifier" description="Your QBOVerifier payment could not be processed. Please try again." noIndex />
      <Header />
      
      <div className="py-20 px-6">
        <div className="max-w-3xl mx-auto">
          {/* Error Icon */}
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-32 h-32 bg-gradient-to-br from-red-400 to-orange-500 rounded-full mb-6 shadow-2xl">
              <i className="ri-close-circle-line text-7xl text-white"></i>
            </div>
            
            <h1 className="text-5xl font-bold text-gray-900 mb-4">
              Payment Failed
            </h1>
            
            <p className="text-2xl text-gray-700 mb-2">
              We couldn't process your payment
            </p>
            
            <p className="text-lg text-gray-600">
              Don't worry, no charges were made to your account
            </p>
          </div>

          {/* Error Details Card */}
          <div className="bg-white rounded-2xl shadow-xl p-8 mb-8 border-2 border-blue-100">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">
              Common Reasons for Payment Failure
            </h2>
            
            <div className="space-y-4">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 flex items-center justify-center bg-blue-100 rounded-full flex-shrink-0">
                  <i className="ri-bank-card-line text-xl text-blue-600"></i>
                </div>
                <div>
                  <h3 className="font-bold text-gray-900 mb-1">Insufficient Funds</h3>
                  <p className="text-gray-600">Your card may not have enough available balance for this transaction.</p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-10 h-10 flex items-center justify-center bg-blue-100 rounded-full flex-shrink-0">
                  <i className="ri-error-warning-line text-xl text-blue-600"></i>
                </div>
                <div>
                  <h3 className="font-bold text-gray-900 mb-1">Incorrect Card Details</h3>
                  <p className="text-gray-600">Please verify your card number, expiry date, and CVV are entered correctly.</p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-10 h-10 flex items-center justify-center bg-blue-100 rounded-full flex-shrink-0">
                  <i className="ri-shield-cross-line text-xl text-blue-600"></i>
                </div>
                <div>
                  <h3 className="font-bold text-gray-900 mb-1">Bank Security Block</h3>
                  <p className="text-gray-600">Your bank may have flagged this as a suspicious transaction. Contact your bank to authorize the payment.</p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-10 h-10 flex items-center justify-center bg-blue-100 rounded-full flex-shrink-0">
                  <i className="ri-calendar-close-line text-xl text-blue-600"></i>
                </div>
                <div>
                  <h3 className="font-bold text-gray-900 mb-1">Expired Card</h3>
                  <p className="text-gray-600">The card you're trying to use may have expired. Please use a different payment method.</p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-10 h-10 flex items-center justify-center bg-blue-100 rounded-full flex-shrink-0">
                  <i className="ri-global-line text-xl text-blue-600"></i>
                </div>
                <div>
                  <h3 className="font-bold text-gray-900 mb-1">International Transaction Restrictions</h3>
                  <p className="text-gray-600">Your card may not be enabled for international transactions. Contact your bank to enable this feature.</p>
                </div>
              </div>
            </div>
          </div>

          {/* What to Do Next */}
          <div className="bg-gradient-to-r from-blue-50 to-blue-10 rounded-2xl p-8 mb-8 border-2 border-blue-100">
            <h2 className="text-2xl font-bold text-gray-900 mb-6 text-center">
              What Should You Do?
            </h2>
            
            <div className="space-y-4">
              <div className="bg-white rounded-xl p-6 shadow-sm">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 flex items-center justify-center bg-blue-100 rounded-full flex-shrink-0">
                    <span className="text-xl font-bold text-blue-600">1</span>
                  </div>
                  <div>
                    <h3 className="font-bold text-gray-900 mb-1">Verify Your Payment Information</h3>
                    <p className="text-gray-600">Double-check your card details and billing address</p>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-xl p-6 shadow-sm">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 flex items-center justify-center bg-blue-100 rounded-full flex-shrink-0">
                    <span className="text-xl font-bold text-blue-600">2</span>
                  </div>
                  <div>
                    <h3 className="font-bold text-gray-900 mb-1">Contact Your Bank</h3>
                    <p className="text-gray-600">Ensure your card is active and has sufficient funds</p>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-xl p-6 shadow-sm">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 flex items-center justify-center bg-blue-100 rounded-full flex-shrink-0">
                    <span className="text-xl font-bold text-blue-600">3</span>
                  </div>
                  <div>
                    <h3 className="font-bold text-gray-900 mb-1">Try Again or Use Different Payment Method</h3>
                    <p className="text-gray-600">Retry with the same card or choose an alternative payment option</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-8">
            <button
              onClick={() => navigate('/pricing')}
              className="px-8 py-4 text-white rounded-lg font-semibold text-lg transition-all hover:shadow-xl whitespace-nowrap cursor-pointer"
              style={{ backgroundColor: '#29B3E6' }}
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#2563EB'}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#29B3E6'}
            >
              <i className="ri-refresh-line mr-2"></i>
              Try Again
            </button>

            <button
              onClick={() => navigate('/')}
              className="px-8 py-4 bg-white text-gray-700 rounded-lg font-semibold text-lg border-2 border-gray-300 hover:border-gray-400 hover:shadow-lg transition-all whitespace-nowrap cursor-pointer"
            >
              <i className="ri-home-line mr-2"></i>
              Return to Home
            </button>
          </div>

          {/* Help Section */}
          <div className="bg-white rounded-2xl shadow-lg p-8 border-2 border-gray-200">
            <div className="text-center">
              <div className="w-16 h-16 flex items-center justify-center bg-blue-100 rounded-full mx-auto mb-4">
                <i className="ri-customer-service-2-line text-3xl text-blue-600"></i>
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-3">
                Need Assistance?
              </h2>
              <p className="text-gray-600 mb-6">
                Our support team is here to help you complete your purchase. We're available to answer any questions or resolve payment issues.
              </p>
              <button
                onClick={() => navigate('/contact')}
                className="px-8 py-3 bg-gradient-to-r from-blue-500 to-teal-500 text-white rounded-lg font-semibold text-lg hover:shadow-xl transition-all whitespace-nowrap cursor-pointer"
              >
                <i className="ri-mail-line mr-2"></i>
                Contact Support
              </button>
            </div>
          </div>

          {/* Reassurance Message */}
          <div className="text-center mt-8 pt-8 border-t border-gray-200">
            <div className="flex items-center justify-center gap-2 text-gray-600">
              <i className="ri-shield-check-line text-2xl text-green-600"></i>
              <p>
                Your payment information is secure and encrypted. No charges were made to your account.
              </p>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}
